import { SpotApiError } from './spotsErrors';

const STORAGE_KEY = 'deinImker.mockSpots';
const PENDING = process.env.REACT_APP_SPOTS_MOCK_PENDING === '1';
const DUPLICATE_RADIUS_M = 50;
const LATENCY_MS = 400;

const wait = () => new Promise(resolve => setTimeout(resolve, LATENCY_MS));

const readAll = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
        return [];
    }
};

const writeAll = (spots) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(spots));
    } catch {
        throw new SpotApiError('request_failed', 'Der lokale Speicher ist voll.');
    }
};

const geraetekennung = () => {
    let kennung = localStorage.getItem('deinImker.mockVoter');
    if (!kennung) {
        kennung = randomToken(16);
        localStorage.setItem('deinImker.mockVoter', kennung);
    }
    return kennung;
};

const randomToken = (length) => {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').slice(0, length);
};

const distanceInMeters = (a, b) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
};

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new SpotApiError('request_failed', 'Das Foto konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
});

const toClient = (spot) => ({
    id: spot.id,
    lat: spot.lat,
    lng: spot.lon,
    municipality: spot.municipality,
    note: spot.note,
    assortment: spot.assortment,
    spotType: spot.spotType || null,
    votingSupported: true,
    confirmations: (spot.votes || []).filter(v => v.vote === 'exists').length,
    doubts: (spot.votes || []).filter(v => v.vote === 'gone').length,
    myVote: (spot.votes || []).find(v => v.voter === geraetekennung())?.vote || null,
    photoUrl: spot.photoUrl,
    status: spot.status,
    source: spot.source,
    createdAt: spot.createdAt,
    claimedLocationId: spot.claimedLocationId
});

export const fetchReportedSpots = async () => {
    await wait();
    const spots = readAll().filter(spot => spot.status === 'published');
    return {
        meta: { total: spots.length, limit: 200, offset: 0 },
        spots: spots.map(toClient)
    };
};

export const fetchReportedSpot = async (id, editToken) => {
    await wait();
    const spot = readAll().find(entry => entry.id === id);
    if (!spot || (spot.status !== 'published' && spot.editToken !== editToken)) {
        throw new SpotApiError('not_found', 'Dieser Eintrag wurde nicht gefunden.');
    }
    return {
        spot: toClient(spot),
        canEdit: Boolean(editToken) && spot.editToken === editToken
            && ['published', 'pending_review'].includes(spot.status)
    };
};

export const reportSpot = async ({ photo, lat, lng, note, assortment, email, spotType }) => {
    await wait();
    const spots = readAll();

    const nearby = spots.find(spot =>
        spot.status === 'published' &&
        distanceInMeters({ lat: spot.lat, lng: spot.lon }, { lat, lng }) <= DUPLICATE_RADIUS_M
    );
    if (nearby) {
        throw new SpotApiError('duplicate', 'Diese Stelle ist bereits erfasst.', {
            spot: toClient(nearby)
        });
    }

    const spot = {
        id: randomToken(12),
        editToken: randomToken(32),
        lat,
        lon: lng,
        municipality: `Testgemeinde (${lat.toFixed(3)}, ${lng.toFixed(3)})`,
        note: note || '',
        assortment: assortment || [],
        spotType: spotType || null,
        votes: [],
        photoUrl: await readAsDataUrl(photo),
        status: PENDING ? 'pending_review' : 'published',
        source: 'public',
        email: email || '',
        createdAt: new Date().toISOString(),
        claimedLocationId: null
    };

    writeAll([...spots, spot]);

    return {
        spot: toClient(spot),
        editToken: spot.editToken
    };
};

export const withdrawSpot = async (id, editToken) => {
    await wait();
    const spots = readAll();
    const spot = spots.find(entry => entry.id === id);
    if (!spot) throw new SpotApiError('not_found', 'Dieser Eintrag wurde nicht gefunden.');
    if (spot.editToken !== editToken) throw new SpotApiError('forbidden', 'Der Link ist nicht gültig.');
    if (!['published', 'pending_review'].includes(spot.status)) {
        throw new SpotApiError('already_claimed', 'Dieser Eintrag lässt sich nicht mehr zurückziehen.');
    }

    spot.status = 'removed';
    spot.photoUrl = null;
    writeAll(spots);
    return true;
};

export const voteSpot = async (id, vote) => {
    await wait();
    const spots = readAll();
    const spot = spots.find(entry => entry.id === id);
    if (!spot) throw new SpotApiError('not_found', 'Dieser Eintrag wurde nicht gefunden.');
    if (spot.status !== 'published') {
        throw new SpotApiError('not_votable', 'Für diesen Eintrag lässt sich nicht abstimmen.');
    }

    spot.votes = spot.votes || [];
    if (spot.votes.some(v => v.voter === geraetekennung())) {
        throw new SpotApiError('already_voted', 'Du hast für diesen Eintrag schon abgestimmt.', {
            spot: toClient(spot)
        });
    }

    spot.votes.push({ voter: geraetekennung(), vote });
    writeAll(spots);
    return { spot: toClient(spot), voted: vote };
};

export const submitObjection = async (id) => {
    await wait();
    const spot = readAll().find(entry => entry.id === id);
    if (!spot) throw new SpotApiError('not_found', 'Dieser Eintrag wurde nicht gefunden.');
    return true;
};
